import { Assets, Graphics, Rectangle, Sprite, Texture } from 'pixi.js';
import type { StatusApply } from '../core/types';
import { FX_RAMPS } from '../core/skillfx';
import { audio } from '../core/audio';
import type { Enemy } from './entities';
import type { WeaponHost } from './weapons';

/**
 * 다중 소환 유지 — 원소 정령의 그림과 「정령왕」.
 *
 * 정령(궤도를 도는 것)의 그림: public/assets/fx/spirits.png (tools/draw_spirits.py 가 찍는다)
 * 정령왕 그림:                public/assets/kings/king_<원소>.png (tools/slice_kings.py 가 받은 시트에서 자른다)
 *
 * 정령왕 셋(불·얼음·번개)은 궤도를 돌지 않는다. 캐릭터 둘레 넓은 반경(LEASH) 안을 떠다니며 제각기 싸운다.
 *   불   — 몰려 있는 곳으로 파고드는 싸움꾼. 멀면 화염구, 붙으면 주먹, 세 번째마다 지면 강타(불바닥)
 *   얼음 — 캐릭터에게 가장 가까운 적을 막는 수비수. 얼음 파동(꿰뚫음), 붙으면 할퀴기, 세 번째마다 얼음 가시(얼린다)
 *   번개 — 가장 센 적(보스·엘리트)을 노리는 저격수. 연쇄 번개, 붙으면 초승달, 세 번째마다 광선 또는 낙뢰
 * 부자연스럽지 않게: 가속·감속하며 움직이고(순간 방향 전환 없음), 서로 부딪히지 않게 벌어지고, 공격은 준비 자세 → 치는 자세 순서,
 * 떠 있으니 위아래로 천천히 흔들리고 발밑에 그림자가 있다. 캐릭터와 앞뒤(화면 아래가 앞)를 가려 그린다.
 */

export type Element = 'fire' | 'ice' | 'bolt';
export const ELEMENTS: readonly Element[] = ['fire', 'ice', 'bolt'];

/** 그림 한 장 — 기준점(ax, ay)은 프레임 안 픽셀 좌표 */
export interface ArtFrame {
  texture: Texture;
  ax: number;
  ay: number;
  w: number;
  h: number;
  /** 정면 그림 — 좌우로 뒤집지 않는다 */
  front: boolean;
}

// ── 그림 읽기 ───────────────────────────────────────────

/** 정령: 'high_fire' 같은 이름 → 4장 */
const spiritArt = new Map<string, ArtFrame[]>();
/** 정령왕: 원소 → 이름 → 한 장 */
const kingArt = new Map<Element, Map<string, ArtFrame>>();

export async function loadSheet(jsonPath: string): Promise<{ meta: Record<string, unknown>; source: Texture['source'] }> {
  const res = await fetch(jsonPath);
  if (!res.ok) throw new Error(`${jsonPath} (${res.status})`);
  const meta = (await res.json()) as Record<string, unknown>;
  const dir = jsonPath.slice(0, jsonPath.lastIndexOf('/') + 1);
  const tex = (await Assets.load(dir + (meta.image as string))) as Texture;
  tex.source.scaleMode = 'nearest';
  tex.source.autoGenerateMipmaps = false;
  return { meta, source: tex.source };
}

type FrameJson = { x: number; y: number; w: number; h: number; ax: number; ay: number; front?: boolean };

function frameOf(source: Texture['source'], f: FrameJson): ArtFrame {
  return {
    texture: new Texture({ source, frame: new Rectangle(f.x, f.y, f.w, f.h) }),
    ax: f.ax, ay: f.ay, w: f.w, h: f.h, front: !!f.front,
  };
}

/** 정령·정령왕 그림을 읽는다. 못 읽으면 정령은 코드로 찍은 구슬로, 정령왕은 안 나온다(경고만). */
export async function loadSpiritArt(): Promise<void> {
  try {
    const { meta, source } = await loadSheet('assets/fx/spirits.json');
    for (const [name, list] of Object.entries(meta.frames as Record<string, FrameJson[]>)) {
      spiritArt.set(name, list.map((f) => frameOf(source, f)));
    }
  } catch (e) {
    console.warn('[art] 정령 그림을 못 읽었다. 코드로 찍은 구슬로 대신한다.', e);
  }
  for (const el of ELEMENTS) {
    try {
      const { meta, source } = await loadSheet(`assets/kings/king_${el}.json`);
      const m = new Map<string, ArtFrame>();
      for (const [name, f] of Object.entries(meta.frames as Record<string, FrameJson>)) m.set(name, frameOf(source, f));
      kingArt.set(el, m);
    } catch (e) {
      console.warn(`[art] ${el} 정령왕 그림을 못 읽었다.`, e);
    }
  }
}

/** 정령 그림 4장 (없으면 null) */
export function spiritFrames(tier: 'low' | 'high', el: Element): ArtFrame[] | null {
  return spiritArt.get(`${tier}_${el}`) ?? null;
}

/** 정령왕 그림 한 장 (없으면 null) — 도감용 */
export function kingFrame(el: Element, name: string): ArtFrame | null {
  return kingArt.get(el)?.get(name) ?? null;
}

/** 스프라이트에 그림 한 장을 입힌다 (기준점 맞춤) */
export function applyFrame(sp: Sprite, f: ArtFrame): void {
  if (sp.texture !== f.texture) sp.texture = f.texture;
  sp.anchor.set(f.ax / f.w, f.ay / f.h);
}

// ── 정령왕 ─────────────────────────────────────────────

/** 캐릭터에게서 이만큼 안의 적을 노린다 */
const ENGAGE = 300;
/** 정령왕이 캐릭터에게서 이보다 멀리 가지 않는다 */
const LEASH = 340;
/** 이보다 멀어지면(캐릭터가 도약·순간이동했을 때) 곁으로 순간이동한다 */
const TELEPORT = 640;
/** 할 일이 없을 때 캐릭터 둘레의 자리 (반경) */
const HOME_R = 110;
/** 떠다니는 속도·쫓아갈 때 속도·가속도 (px/s, px/s²) */
const SPEED = 240;
const CATCHUP = 470;
const ACCEL = 1500;
/** 서로 이보다 가까우면 밀어낸다 / 캐릭터와 이보다 가까우면 비킨다 (정령왕 그림 폭이 90~110px 라 넉넉히) */
const APART = 108;
const PLAYER_ROOM = 92;
/** 바닥에서 떠 있는 높이(px) */
const HOVER = 6;

const TAU = Math.PI * 2;

const LOOK: Record<Element, { ramp: readonly string[]; attr: string; shadowW: number; burstStyle: 'fire' | 'spark' }> = {
  fire: { ramp: FX_RAMPS.flame, attr: 'red', shadowW: 44, burstStyle: 'fire' },
  ice: { ramp: FX_RAMPS.ice, attr: 'blue', shadowW: 50, burstStyle: 'spark' },
  bolt: { ramp: FX_RAMPS.violet, attr: 'yellow', shadowW: 40, burstStyle: 'spark' },
};

/**
 * 등장 연출 (게임 시간, 초): 고위 정령들이 삼각형 세 꼭짓점으로 날아가 모이고(GATHER) → 화면이 깜빡깜빡(BLINKS) →
 * 크게 번쩍(REVEAL)하는 순간 정령왕 셋이 한꺼번에 떠 있다. 바닥에는 소환 삼각진이 잠깐 빛난다.
 */
const GATHER = 0.45;
const BLINKS = [0.45, 0.53];
const REVEAL = 0.62;
/** 삼각 대형 — 불은 위, 얼음은 오른쪽 아래, 번개는 왼쪽 아래 (캐릭터 둘레) */
const TRI_R = 125;
const SLOT_ANGLE = [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6];

/** 떠 있는 채로 캐릭터를 따라 흘러가며 쓰는 기술 (멀리 쏘는 것) */
const CARRY = new Set(['shot', 'wave', 'zap', 'strike']);

/** 목표를 다시 고르는 간격(초) — 짧으면 이리저리 흔들린다. 목표가 죽거나 멀어지면 바로 다시 고른다 */
const RETARGET: Record<Element, number> = { fire: 1.6, ice: 0.6, bolt: 1.2 };

/** 쉬고·떠다니는 동안 그림 (정면) */
const IDLE: Record<Element, string[]> = {
  fire: ['idle0', 'idle1', 'idle2', 'idle3'],
  ice: ['idle0', 'idle1', 'idle3', 'idle1'],
  bolt: ['idle0', 'idle1', 'idle3', 'idle1'],
};
/** 옆으로 움직일 때 (오른쪽을 보고 그렸다) */
const MOVE: Record<Element, string[]> = {
  fire: ['move0', 'move1', 'move2', 'move3', 'move4'],
  ice: ['move0', 'move1', 'move2', 'move3', 'move4', 'move5', 'move6', 'move7'],
  bolt: ['side'],
};
/** 왼쪽을 보고 그린 프레임 (오른쪽을 볼 때 뒤집는다) */
const FACES_LEFT = new Set(['fire:hook']);

/** 한 동작(공격·등장) — 시간에 따라 자세를 바꾸고, 정해진 순간에 일을 한다 */
interface Act {
  kind: string;
  t: number;
  dur: number;
  /** [이 시각까지, 이 그림] 차례로 */
  poses: Array<[number, string]>;
  /** [이 시각에, 할 일] — 한 번씩 */
  events: Array<[number, () => void]>;
  next: number;
  /** 동작 동안 몸을 앞으로 내미는 거리(px, 시간에 따라) */
  lunge?: (t: number) => number;
  /** 동작 동안 떠 있는 높이에 더하는 것 */
  lift?: (t: number) => number;
}

interface King {
  el: Element;
  art: Map<string, ArtFrame>;
  sprite: Sprite;
  shadow: Graphics;
  slot: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  face: 1 | -1;
  phase: number;
  animT: number;
  alpha: number;
  /** 'wait' = 번쩍하기 전(안 보임), 'summon' = 나타나는 중, 'free' = 떠다니며 싸움, 'blink' = 순간이동 중 */
  mode: 'wait' | 'summon' | 'free' | 'blink';
  wait: number;
  act: Act | null;
  cd: number;
  /** 지금까지 한 공격 수 (세 번째마다 큰 기술) */
  count: number;
  target: Enemy | null;
  retarget: number;
  /** 큰 기술을 하려고 다가가는 중인 시간 — 오래 걸리면 포기하고 보통 공격 */
  heavyWait: number;
  /** 지금 보여 주는 그림 이름 (확인용) */
  pose: string;
  /** 움직이는 그림을 몇 장째까지 넘겼나 (속도에 따라 빨리 넘긴다 — 시간×배율로 하면 속도가 바뀔 때 거꾸로 넘어간다) */
  stride: number;
  /** 움직이는 그림을 쓰는 중인가 (멈춤·움직임 사이에서 깜빡이지 않게 문턱을 둘로 둔다) */
  moving: boolean;
}

/** 날아가는 것 (불의 화염구, 얼음 파동) */
interface Shot {
  kind: 'fireball' | 'wave';
  sprite: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  radius: number;
  damage: number;
  status: StatusApply;
  seen: Set<number>;
  /** 꼬리 불티·서리를 다음에 남길 때까지 */
  trail: number;
}

/** 하늘에서 떨어진 번개 그림 (번개의 낙뢰·등장) */
interface StrikeFx {
  sprite: Sprite;
  t: number;
}

/** 등장 연출: 삼각형 꼭짓점으로 날아가 모이는 고위 정령 하나 */
interface Gatherer {
  sprite: Sprite;
  frames: ArtFrame[];
  el: Element;
  /** 캐릭터 기준 출발 각도 */
  a0: number;
  trail: number;
}

export class SpiritCourt {
  private readonly kings: King[] = [];
  private readonly shots: Shot[] = [];
  private readonly strikes: StrikeFx[] = [];
  private host!: WeaponHost;
  /** 이번 프레임 정령왕 한 방의 기준 피해 (정령 피해 × king 배율) */
  private kd = 0;
  private areaMul = 1;
  private time = 0;
  /** 등장 연출이 시작된 뒤 흐른 시간(초). REVEAL 을 지나면 정령왕이 싸운다 */
  private introT = 0;
  private revealed = false;
  private readonly gatherers: Gatherer[] = [];
  /** 소환 삼각진 */
  private glyph: Graphics | null = null;
  /** 할 일 없을 때 서는 세 자리가 도는 각도 (등장한 뒤부터 천천히) */
  private rot = 0;
  /** 캐릭터가 움직이는 속도 (부드럽게) — 달려가는 쪽을 알아야 뒤에 남은 적을 쫓다가 처지지 않는다 */
  private pvx = 0;
  private pvy = 0;
  private lastPx = NaN;
  private lastPy = NaN;
  /** 이번 프레임 캐릭터가 옮겨 간 만큼 — 나타나는 동안은 삼각 대형 그대로 캐릭터를 따라간다 */
  private pdx = 0;
  private pdy = 0;

  /** 그림을 못 읽었으면 정령왕을 만들지 않는다 */
  static available(): boolean {
    return ELEMENTS.every((el) => kingArt.has(el));
  }

  /** @param gather 등장할 때 날아와 모이는 고위 정령 수 (궤도를 돌던 정령들 — 원소마다 고르게) */
  constructor(host: WeaponHost, gather = 6) {
    this.host = host;
    ELEMENTS.forEach((el, i) => {
      const art = kingArt.get(el)!;
      const sprite = new Sprite(art.get(IDLE[el][0])!.texture);
      sprite.visible = false;
      host.actorLayer.addChild(sprite);
      const shadow = new Graphics();
      const w = LOOK[el].shadowW;
      shadow.ellipse(0, 0, w / 2, w / 8).fill({ color: 0x000000, alpha: 0.32 });
      shadow.ellipse(0, 0, w / 3, w / 12).fill({ color: 0x000000, alpha: 0.18 });
      shadow.visible = false;
      host.groundLayer.addChild(shadow);
      const home = this.homeOf(i, host);
      this.kings.push({
        el, art, sprite, shadow, slot: i,
        x: home.x, y: home.y, vx: 0, vy: 0, face: i === 1 ? -1 : 1, phase: i * 2.1, animT: i * 0.37, alpha: 0,
        mode: 'wait', wait: Infinity, act: null, cd: 0.5 + i * 0.25, count: 0,
        target: null, retarget: 0, heavyWait: 0, pose: '', stride: 0, moving: false,
      });
    });
    // 궤도를 돌던 고위 정령들 — 캐릭터 둘레에서 출발해 제 원소의 꼭짓점으로 날아간다
    const n = Math.max(3, gather);
    for (let i = 0; i < n; i++) {
      const el = ELEMENTS[i % 3];
      const frames = spiritFrames('high', el);
      if (!frames) break;
      const sp = new Sprite(frames[0].texture);
      sp.visible = false;
      host.fxLayer.addChild(sp);
      this.gatherers.push({ sprite: sp, frames, el, a0: -Math.PI / 2 + (i / n) * TAU, trail: 0 });
    }
  }

  destroy(): void {
    for (const g of this.gatherers) g.sprite.destroy();
    this.gatherers.length = 0;
    this.glyph?.destroy();
    this.glyph = null;
    for (const k of this.kings) {
      k.sprite.destroy();
      k.shadow.destroy();
    }
    this.kings.length = 0;
    for (const s of this.shots) s.sprite.destroy();
    this.shots.length = 0;
    for (const s of this.strikes) s.sprite.destroy();
    this.strikes.length = 0;
  }

  /** 정령왕이 거는 [화염] 한 중첩의 피해(다중 소환 유지 무기가 매번 넣어 준다) */
  burnDmg = 0;

  /** @param kd 정령왕 한 방의 기준 피해 (정령 피해 × king 배율, 플레이어 배율 포함) */
  update(host: WeaponHost, dt: number, kd: number): void {
    this.host = host;
    this.kd = kd;
    this.areaMul = host.playerStats.areaMul;
    this.time += dt;
    this.pdx = Number.isNaN(this.lastPx) ? 0 : host.px - this.lastPx;
    this.pdy = Number.isNaN(this.lastPy) ? 0 : host.py - this.lastPy;
    if (!Number.isNaN(this.lastPx) && dt > 0) {
      // 한 프레임에 크게 뛴 것(도약·순간이동)은 달리는 속도가 아니다 — 빼고, 속도에도 상한을 둔다
      let vx = (host.px - this.lastPx) / dt;
      let vy = (host.py - this.lastPy) / dt;
      const v = Math.hypot(vx, vy);
      if (v > 900) vx = vy = 0;
      else if (v > 420) {
        vx *= 420 / v;
        vy *= 420 / v;
      }
      const k = Math.min(1, dt * 6);
      this.pvx += (vx - this.pvx) * k;
      this.pvy += (vy - this.pvy) * k;
    }
    this.lastPx = host.px;
    this.lastPy = host.py;
    if (!this.revealed || this.glyph) this.updateIntro(dt);
    if (this.revealed) this.rot += dt * 0.22;
    for (const k of this.kings) this.updateKing(k, dt);
    this.updateShots(dt);
    this.updateStrikes(dt);
  }

  // ── 자리·목표 ────────────────────────────────────────

  /** 할 일 없을 때 서 있을 자리 — 캐릭터 둘레 삼각 대형. 등장한 뒤로 천천히 돈다 */
  private homeOf(slot: number, host: WeaponHost): { x: number; y: number } {
    const a = SLOT_ANGLE[slot] + this.rot;
    return { x: host.px + Math.cos(a) * HOME_R, y: host.py + Math.sin(a) * HOME_R * 0.8 };
  }

  /** 삼각형 꼭짓점 (등장 자리) */
  private vertex(slot: number): { x: number; y: number } {
    const h = this.host;
    const a = SLOT_ANGLE[slot];
    return { x: h.px + Math.cos(a) * TRI_R, y: h.py + Math.sin(a) * TRI_R * 0.85 };
  }

  // ── 등장 연출 ────────────────────────────────────────

  private updateIntro(dt: number): void {
    const h = this.host;
    const t0 = this.introT;
    this.introT += dt;
    const t = this.introT;
    const at = (x: number): boolean => t0 < x && t >= x;
    if (t0 === 0) {
      // 시작: 잠깐 느려지고, 모여드는 소리 — summonRise 는 받은 소리를 거꾸로 튼 것이라 점점 차올라
      // 번쩍하기 직전(실제 시간 약 1.2초 = 느려짐 1.1초 × 0.4배 + 되돌아오는 0.25초)에 빨려 들며 끊긴다
      h.slowMotion(1.1, 0.4);
      audio.play('summonRise');
      audio.play('sigil', { gain: 1.4 });
      audio.play('chime', { rate: 0.7, gain: 1.4 });
    }
    // 고위 정령들이 꼭짓점으로 — 캐릭터 둘레를 휘감으며 처음엔 천천히, 갈수록 빠르게 들어간다
    for (const g of this.gatherers) {
      if (t >= GATHER) {
        g.sprite.visible = false;
        continue;
      }
      const slot = ELEMENTS.indexOf(g.el);
      const u = t / GATHER;
      const e = u * u * (3 - 2 * u);
      const r0 = 150;
      const sx = h.px + Math.cos(g.a0 + e * 1.2) * r0;
      const sy = h.py + Math.sin(g.a0 + e * 1.2) * r0 * 0.85;
      const v = this.vertex(slot);
      const x = sx + (v.x - sx) * e;
      const y = sy + (v.y - 30 - sy) * e;
      g.sprite.visible = true;
      applyFrame(g.sprite, g.frames[Math.floor(t * 12) % g.frames.length]);
      g.sprite.position.set(Math.round(x), Math.round(y));
      g.trail -= dt;
      if (g.trail <= 0) {
        g.trail = 0.05;
        const look = LOOK[g.el];
        h.burst(x, y, 14, look.attr, g.el === 'fire' ? 'ember' : 'spark', look.ramp);
      }
    }
    // 바닥의 소환 삼각진
    if (t >= 0.28 && t < 1.7 && !this.glyph) {
      this.glyph = new Graphics();
      h.groundLayer.addChild(this.glyph);
    }
    if (this.glyph) this.drawGlyph(t);
    // 깜빡깜빡
    for (const b of BLINKS) {
      if (!at(b)) continue;
      h.flashScreen(0.06, b === BLINKS[0] ? 0.55 : 0.35, 0xffffff);
      for (let i = 0; i < 3; i++) {
        const v = this.vertex(i);
        const look = LOOK[ELEMENTS[i]];
        h.burst(v.x, v.y - 30, 46, look.attr, look.burstStyle, look.ramp);
      }
      audio.play('zap', { rate: 0.6, gain: 1.2 });
      audio.play('summonBlink', b === BLINKS[0] ? undefined : { rate: 1.12, gain: 0.8 });
    }
    // 번쩍! — 정령왕 셋이 한꺼번에
    if (at(REVEAL)) {
      this.revealed = true;
      h.flashScreen(0.4, 0.9, 0xfff6e0);
      h.shakeScreen(0.45, 10);
      h.banner('정령왕', '불 · 얼음 · 번개', '#ffd36b');
      for (const k of this.kings) this.beginSummon(k);
      // 받은 소환 소리(반짝 → 쉬익 → 낮은 울림, 3.6초)가 중심. 번개 "콰"가 앞머리를 때리고, 불·얼음이 원소 색을 얹는다
      audio.play('summon');
      audio.play('thunder', { gain: 0.9, rate: 0.9 });
      audio.play('boomFire', { rate: 0.6, gain: 0.9 });
      audio.play('shatter', { rate: 0.5, gain: 0.9 });
    }
    if (t >= 1.7 && this.glyph) {
      this.glyph.destroy();
      this.glyph = null;
    }
  }

  /** 소환 삼각진: 세 꼭짓점을 잇는 빛줄과 둥근 테두리, 꼭짓점마다 원소 색 고리 */
  private drawGlyph(t: number): void {
    const g = this.glyph!;
    g.clear();
    const fadeIn = Math.min(1, (t - 0.28) / 0.2);
    const fadeOut = t > 1.1 ? Math.max(0, 1 - (t - 1.1) / 0.6) : 1;
    const a = fadeIn * fadeOut;
    if (a <= 0) return;
    const h = this.host;
    const v = [0, 1, 2].map((i) => this.vertex(i));
    const pulse = t >= REVEAL && t < REVEAL + 0.25 ? 1.6 : 1;
    const tri = (w: number, color: number, alpha: number): void => {
      g.moveTo(Math.round(v[0].x), Math.round(v[0].y));
      g.lineTo(Math.round(v[1].x), Math.round(v[1].y));
      g.lineTo(Math.round(v[2].x), Math.round(v[2].y));
      g.closePath();
      g.stroke({ width: w, color, alpha });
    };
    tri(10 * pulse, 0xffd36b, 0.18 * a);
    tri(3, 0xfff1c0, 0.75 * a);
    const R = TRI_R * 1.02;
    g.ellipse(Math.round(h.px), Math.round(h.py), R, R * 0.85).stroke({ width: 2, color: 0xfff1c0, alpha: 0.55 * a });
    g.ellipse(Math.round(h.px), Math.round(h.py), R * 1.12, R * 0.95).stroke({ width: 1, color: 0xffd36b, alpha: 0.35 * a });
    const col = [0xff8a1f, 0x7fb2f0, 0xa98bff];
    v.forEach((p, i) => {
      g.ellipse(Math.round(p.x), Math.round(p.y), 30 * pulse, 12 * pulse).stroke({ width: 3, color: col[i], alpha: 0.8 * a });
      g.ellipse(Math.round(p.x), Math.round(p.y), 18, 7).fill({ color: col[i], alpha: 0.25 * a });
    });
  }

  private alive(e: Enemy | null): e is Enemy {
    return !!e && !e.dead && e.hp > 0;
  }

  /** 캐릭터가 달리는 중이면 뒤로 처진 적인가 (쫓아가면 정령왕도 처진다) */
  private behind(e: Enemy): boolean {
    const h = this.host;
    const v = Math.hypot(this.pvx, this.pvy);
    if (v < 80) return false;
    return ((e.x - h.px) * this.pvx + (e.y - h.py) * this.pvy) / v < -70;
  }

  /** 원소마다 노리는 적이 다르다 — 셋이 한 놈에게 몰리지 않게. 캐릭터가 달리는 중이면 뒤에 남은 적은 빼고 */
  private pickTarget(k: King): Enemy | null {
    const e = this.pickTargetRaw(k);
    if (e && this.behind(e)) {
      const h = this.host;
      // 달려가는 쪽 앞의 적
      const ax = h.px + this.pvx * 0.7;
      const ay = h.py + this.pvy * 0.7;
      const f = h.nearestEnemy(ax, ay, ENGAGE);
      return f && !this.behind(f) ? f : null;
    }
    return e;
  }

  private pickTargetRaw(k: King): Enemy | null {
    const h = this.host;
    if (k.el === 'fire') {
      // 가장 몰려 있는 곳
      const spot = h.densestSpot(h.px, h.py, ENGAGE);
      return spot ? h.nearestEnemy(spot.x, spot.y, 200) : null;
    }
    if (k.el === 'ice') {
      // 캐릭터에게 가장 가까운 적 (막아선다)
      return h.nearestEnemy(h.px, h.py, ENGAGE);
    }
    // 번개: 보스 > 엘리트 > 체력 많은 적
    return h.strongestEnemy(h.px, h.py, ENGAGE + 40) ?? h.nearestEnemy(k.x, k.y, ENGAGE);
  }

  // ── 한 마리 ──────────────────────────────────────────

  private updateKing(k: King, dt: number): void {
    const h = this.host;
    k.animT += dt;
    k.stride += dt * (6 + Math.hypot(k.vx, k.vy) / 35);
    switch (k.mode) {
      case 'wait':
        k.wait -= dt;
        if (k.wait <= 0) this.beginSummon(k);
        this.draw(k);
        return;
      case 'summon':
        k.x += this.pdx;
        k.y += this.pdy;
        this.runAct(k, dt);
        this.draw(k);
        return;
      case 'blink':
        this.runAct(k, dt);
        this.draw(k);
        return;
      default:
        break;
    }

    // 너무 멀어졌다(캐릭터가 도약·순간이동) — 하던 동작을 접고 곁으로 순간이동
    const pd = Math.hypot(k.x - h.px, k.y - h.py);
    if (pd > TELEPORT) {
      k.act = null;
      k.cd = Math.max(k.cd, 0.4);
      this.beginBlink(k);
      this.draw(k);
      return;
    }

    if (k.act) {
      this.runAct(k, dt);
      // 동작 중에는 멈춘다(살짝 미끄러지다 선다). 멀리 쏘는 기술은 떠 있는 채로 캐릭터를 따라 흘러가며 쏜다 — 달릴 때 처지지 않게.
      // 붙어서 치는 기술도 조금은 따라 흘러간다(공격이 잦아져서, 안 그러면 달릴 때 뒤로 처진다)
      const carry = k.act ? (CARRY.has(k.act.kind) ? 0.7 : 0.35) : 0;
      const damp = Math.exp(-12 * dt);
      k.vx = this.pvx * carry + (k.vx - this.pvx * carry) * damp;
      k.vy = this.pvy * carry + (k.vy - this.pvy * carry) * damp;
      k.x += k.vx * dt;
      k.y += k.vy * dt;
      this.draw(k);
      return;
    }

    k.cd -= dt;
    k.retarget -= dt;
    if (k.count % 3 === 2) k.heavyWait += dt;
    if (!this.alive(k.target) || k.retarget <= 0 || Math.hypot(k.target.x - h.px, k.target.y - h.py) > LEASH + 40 || this.behind(k.target)) {
      k.target = this.pickTarget(k);
      k.retarget = RETARGET[k.el];
    }

    // 어디로 갈까 — 캐릭터에게서 많이 떨어졌으면(캐릭터가 달려갔다) 싸움은 두고 먼저 따라붙는다
    let gx: number;
    let gy: number;
    let stopAt = 6;
    // 달리는 중이면 더 일찍 따라붙기 시작한다 (따라붙는 속도에 이를 때까지 더 처지니까)
    const running = Math.hypot(this.pvx, this.pvy) > 80;
    const regroup = pd > LEASH * (running ? 0.72 : 0.85);
    const t = !regroup && this.alive(k.target) ? k.target : null;
    if (t) {
      const want = this.intent(k, t);
      if (k.cd <= 0 && want.ready) {
        this.startAttack(k, t, want.kind);
        this.draw(k);
        return;
      }
      if (want.align) {
        // 옆으로 곧게 뻗는 기술(광선) — 목표와 같은 높이, 옆으로 사거리만큼 떨어진 자리
        gx = t.x + (k.x >= t.x ? 1 : -1) * want.range;
        gy = t.y;
      } else {
        // 사거리 끝 조금 안쪽에 선다 (지금 자리 쪽에서 다가간다)
        const dx = k.x - t.x;
        const dy = k.y - t.y;
        const d = Math.hypot(dx, dy) || 1;
        const r = want.range * 0.85;
        gx = t.x + (dx / d) * Math.min(r, d);
        gy = t.y + (dy / d) * Math.min(r, d);
      }
      stopAt = 4;
    } else {
      const home = this.homeOf(k.slot, h);
      gx = home.x;
      gy = home.y;
      stopAt = 14;
    }
    // 캐릭터에게서 너무 멀리 가지 않고, 캐릭터 몸 위에도 서지 않는다
    const lx = gx - h.px;
    const ly = gy - h.py;
    const ld = Math.hypot(lx, ly) || 0.01;
    if (ld > LEASH) {
      gx = h.px + (lx / ld) * LEASH;
      gy = h.py + (ly / ld) * LEASH;
    } else if (ld < PLAYER_ROOM + 8) {
      // 적이 캐릭터 코앞이면 캐릭터 옆(지금 서 있는 쪽)에서 친다
      const sx = k.x - h.px;
      const sy = k.y - h.py;
      const sd = Math.hypot(sx, sy) || 1;
      gx = h.px + (sx / sd) * (PLAYER_ROOM + 8);
      gy = h.py + (sy / sd) * (PLAYER_ROOM + 8);
    }
    this.steer(k, gx, gy, stopAt, regroup ? CATCHUP : SPEED, dt);
    this.draw(k);
  }

  /** 가속·감속하며 목표 자리로 (도착하면 부드럽게 선다). 서로·캐릭터와 겹치지 않게 벌어진다 */
  private steer(k: King, gx: number, gy: number, stopAt: number, speed: number, dt: number): void {
    const h = this.host;
    const dx = gx - k.x;
    const dy = gy - k.y;
    const d = Math.hypot(dx, dy);
    let wx = 0;
    let wy = 0;
    if (d > stopAt) {
      const s = speed * Math.min(1, (d - stopAt) / 90); // 가까워지면 느려진다
      wx = (dx / d) * s;
      wy = (dy / d) * s;
    }
    // 서로 밀어내기
    for (const o of this.kings) {
      if (o === k || o.mode === 'wait') continue;
      const ox = k.x - o.x;
      const oy = k.y - o.y;
      const od = Math.hypot(ox, oy);
      if (od < APART && od > 0.01) {
        const p = ((APART - od) / APART) * 420;
        wx += (ox / od) * p;
        wy += (oy / od) * p;
      }
    }
    // 캐릭터 몸 위에 서지 않는다
    const px = k.x - h.px;
    const py = k.y - h.py;
    const pd = Math.hypot(px, py);
    if (pd < PLAYER_ROOM && pd > 0.01) {
      const p = ((PLAYER_ROOM - pd) / PLAYER_ROOM) * 480;
      wx += (px / pd) * p;
      wy += (py / pd) * p;
    }
    // 가속 한도 안에서 바라는 속도로
    const ax = wx - k.vx;
    const ay = wy - k.vy;
    const al = Math.hypot(ax, ay);
    const maxA = ACCEL * dt;
    if (al > maxA) {
      k.vx += (ax / al) * maxA;
      k.vy += (ay / al) * maxA;
    } else {
      k.vx = wx;
      k.vy = wy;
    }
    k.x += k.vx * dt;
    k.y += k.vy * dt;
    // 방향은 옆으로 충분히 움직일 때만 바꾼다 (좌우로 떨지 않게)
    if (Math.abs(k.vx) > 45) k.face = k.vx > 0 ? 1 : -1;
  }

  /** 이 적에게 무엇을 하려는가 — 사거리와 지금 칠 수 있는지 */
  private intent(k: King, t: Enemy): { kind: string; range: number; ready: boolean; align?: boolean } {
    const d = Math.hypot(t.x - k.x, t.y - k.y);
    const heavyDue = k.count % 3 === 2 && k.heavyWait < 2.2;
    const am = this.areaMul;
    if (k.el === 'fire') {
      if (heavyDue) return { kind: 'slam', range: 70, ready: d <= 90 };
      if (d <= 150 * am) return { kind: 'hook', range: 70, ready: d <= 95 };
      return { kind: 'shot', range: 240, ready: d <= 260 };
    }
    if (k.el === 'ice') {
      if (heavyDue) return { kind: 'spike', range: 80, ready: d <= 100 };
      if (d <= 95) return { kind: 'claw', range: 80, ready: true };
      return { kind: 'wave', range: 220, ready: d <= 250 };
    }
    if (heavyDue) {
      // 보스·엘리트가 곁에 있으면 광선, 아니면 낙뢰 (낙뢰는 어디서든)
      if (t.boss || t.elite) {
        const ax = Math.abs(t.x - k.x);
        return { kind: 'beam', range: 140, ready: Math.abs(t.y - k.y) < 26 && ax > 50 && ax < 200, align: true };
      }
      return { kind: 'strike', range: 280, ready: d <= 320 };
    }
    if (d <= 100) return { kind: 'crescent', range: 90, ready: true };
    return { kind: 'zap', range: 280, ready: d <= 310 };
  }

  // ── 그리기 ───────────────────────────────────────────

  private frame(k: King, name: string): ArtFrame {
    return k.art.get(name) ?? k.art.get(IDLE[k.el][0])!;
  }

  private draw(k: King): void {
    const sp = k.sprite;
    let name: string;
    let lift = 0;
    let lunge = 0;
    // 동작의 높이·내밀기를 먼저 — 등장·순간이동은 여기서 투명도도 정한다
    if (k.act) {
      const a = k.act;
      name = a.poses.find(([until]) => a.t < until)?.[1] ?? a.poses[a.poses.length - 1][1];
      lift = a.lift?.(a.t) ?? 0;
      lunge = a.lunge?.(a.t) ?? 0;
    } else {
      name = '';
    }
    const vis = k.mode !== 'wait' && k.alpha > 0.01;
    sp.visible = vis;
    k.shadow.visible = vis;
    if (!vis) return;
    if (!k.act) {
      const speed = Math.hypot(k.vx, k.vy);
      k.moving = speed > (k.moving ? 30 : 60);
      if (k.moving && Math.abs(k.vx) > Math.abs(k.vy) * 0.55) {
        // 옆으로 움직인다 — 빠를수록 빨리 넘긴다. 번개는 빠르면 몸을 눕혀 날아간다
        const list = k.el === 'bolt' && speed > 300 ? ['dash'] : MOVE[k.el];
        name = list[Math.floor(k.stride) % list.length];
      } else if (k.el === 'fire' && k.moving && k.vy < 0) {
        name = 'back'; // 위(화면 안쪽)로 갈 때는 등을 보인다
      } else {
        const list = IDLE[k.el];
        name = list[Math.floor(k.animT * 4) % list.length];
      }
    }
    const f = this.frame(k, name);
    k.pose = name;
    applyFrame(sp, f);
    // 오른쪽을 보고 그린 그림은 왼쪽을 볼 때 뒤집는다. 정면 그림은 그대로
    const leftArt = FACES_LEFT.has(`${k.el}:${name}`);
    sp.scale.x = f.front ? 1 : (k.face < 0) !== leftArt ? -1 : 1;
    // 떠 있다 — 천천히 위아래로 (도트가 떨리지 않게 정수로)
    const bob = Math.round(Math.sin(this.time * 2.4 + k.phase) * 2);
    const hover = HOVER + bob + lift;
    const gx = Math.round(k.x + lunge * k.face);
    const gy = Math.round(k.y);
    sp.position.set(gx, gy - Math.round(hover));
    // 캐릭터 앞(화면 아래)에서 캐릭터를 가리면 비쳐 보이게 — 내 캐릭터를 놓치지 않게
    const h = this.host;
    const cover = gy > h.py + 4 && Math.abs(gx - h.px) < f.w * 0.42 && gy - h.py < f.h * 0.8;
    sp.alpha = k.alpha * (cover ? 0.55 : 1);
    sp.zIndex = gy;
    k.shadow.position.set(gx, gy);
    const sk = Math.max(0.55, 1 - hover / 60);
    k.shadow.scale.set(sk);
    k.shadow.alpha = k.alpha;
  }

  // ── 동작 ─────────────────────────────────────────────

  private runAct(k: King, dt: number): void {
    const a = k.act;
    if (!a) {
      if (k.mode !== 'free') k.mode = 'free';
      return;
    }
    a.t += dt;
    while (a.next < a.events.length && a.t >= a.events[a.next][0]) {
      a.events[a.next][1]();
      a.next++;
    }
    if (a.t >= a.dur) {
      k.act = null;
      if (k.mode === 'summon' || k.mode === 'blink') {
        k.mode = 'free';
        k.alpha = 1;
      }
    }
  }

  private play(k: King, act: Omit<Act, 't' | 'next'>): void {
    act.events.sort((p, q) => p[0] - q[0]);
    k.act = { ...act, t: 0, next: 0 };
  }

  /** 손끝 자리 — 몸 기준점에서 앞으로 fwd, 위로 up */
  private hand(k: King, fwd: number, up: number): { x: number; y: number } {
    return { x: k.x + k.face * fwd, y: k.y - HOVER - up };
  }

  private aim(k: King, t: Enemy, fromUp = 40): { dx: number; dy: number } {
    const dx = t.x - k.x;
    const dy = t.y - (k.y - HOVER - fromUp) + fromUp * 0.6;
    const d = Math.hypot(dx, dy) || 1;
    return { dx: dx / d, dy: dy / d };
  }

  private status(el: Element, heavy = false): StatusApply {
    const ps = this.host.playerStats;
    if (el === 'fire') return { burn: (heavy ? 5 : 3) + ps.burnBonus, burnDmg: this.burnDmg || undefined };
    if (el === 'ice') return { frost: (heavy ? 8 : 3) + ps.frostBonus };
    return heavy ? { shock: 1, stun: 0.45 } : { shock: 1 };
  }

  private startAttack(k: King, t: Enemy, kind: string): void {
    const h = this.host;
    k.face = t.x >= k.x ? 1 : -1;
    const heavy = kind === 'slam' || kind === 'spike' || kind === 'beam' || kind === 'strike';
    k.heavyWait = 0;
    k.count++;
    const kd = this.kd;
    const am = this.areaMul;
    const look = LOOK[k.el];
    const afterCd = heavy ? 0.9 : 0.4 + h.rng.next() * 0.25;
    const done = (): void => {
      k.cd = afterCd;
    };
    /** 지금 노리는 적 (그사이 죽었으면 처음 고른 적) */
    const aimAt = (): Enemy => (this.alive(k.target) ? k.target : t);
    switch (kind) {
      // ── 불 ──
      case 'shot': {
        // 화염구 셋을 부채꼴로 연달아 — 주먹을 번갈아 내지른다
        const volley = (i: number, spread: number) => (): void => {
          const p = this.hand(k, 58, 42);
          const tt = aimAt();
          const a = Math.atan2(tt.y - p.y, tt.x - p.x) + spread;
          const d = Math.hypot(tt.x - p.x, tt.y - p.y);
          this.fire('fireball', p.x, p.y, Math.cos(a), Math.sin(a), 540, Math.min(420, d + 40), 20 * am, kd * 0.7, this.status('fire'));
          h.burst(p.x, p.y, 26, look.attr, 'ember');
          audio.play('fireball', { rate: 0.8 + i * 0.1 });
        };
        this.play(k, {
          kind, dur: 0.66,
          poses: [[0.18, 'side'], [0.26, 'punch'], [0.34, 'thrust'], [0.66, 'punch']],
          lunge: (u) => (u < 0.2 ? -3 : u < 0.42 ? ((u - 0.2) % 0.08 < 0.04 ? 7 : 3) : 3),
          events: [[0.2, volley(0, 0)], [0.28, volley(1, 0.18)], [0.36, volley(2, -0.18)], [0.66, done]],
        });
        break;
      }
      case 'hook': {
        this.play(k, {
          kind, dur: 0.5,
          poses: [[0.12, 'turn'], [0.5, 'hook']],
          lunge: (u) => (u < 0.12 ? -4 : u < 0.22 ? 12 : 8),
          events: [
            [0.14, () => {
              const a = this.aim(k, aimAt(), 30);
              const hits = h.coneDamage(k.x, k.y - 30, a.dx, a.dy, 110 * am, 160, kd * 2.4, 260, this.status('fire'));
              h.burst(k.x + k.face * 60, k.y - 30, 44, look.attr, 'fire', look.ramp);
              h.burst(k.x + k.face * 84, k.y - 24, 22, look.attr, 'ember', undefined, 0.05);
              audio.play('whip', { rate: 0.7, gain: 1.2 });
              if (hits > 0) audio.play('hitFire');
            }],
            [0.5, done],
          ],
        });
        break;
      }
      case 'slam': {
        this.play(k, {
          kind, dur: 0.95,
          poses: [[0.34, 'raise'], [0.95, 'slam']],
          lift: (u) => (u < 0.34 ? (u / 0.34) * 10 : u < 0.4 ? -HOVER : -HOVER + Math.min(1, (u - 0.4) / 0.4) * HOVER),
          events: [
            [0.36, () => {
              const r = 150 * am;
              h.areaDamage(k.x, k.y, r, kd * 3.5, 340, 0, 0, -1, 0, this.status('fire', true));
              // 가운데 큰 불길 → 불 고리가 두 겹으로 퍼져 나간다
              h.burst(k.x, k.y, r * 0.75, look.attr, 'fire', look.ramp);
              this.burstRing(k.x, k.y, 8, r * 0.55, 40, 'fire', look, 0.04);
              this.burstRing(k.x, k.y, 10, r * 0.95, 34, 'fire', look, 0.1);
              for (let i = 0; i < 6; i++) {
                const a = (i / 6) * TAU + h.rng.next();
                h.spawnCinder(k.x + Math.cos(a) * r * 0.55, k.y + Math.sin(a) * r * 0.45, 46, kd * 0.12, { burn: 2, burnDmg: this.burnDmg || undefined });
              }
              h.flashScreen(0.12, 0.22, 0xff9a3c);
              h.shakeScreen(0.22, 7);
              h.heavyHit();
              audio.play('hammer', { rate: 0.6, gain: 1.3 });
              audio.play('boomFire', { rate: 0.7, gain: 1.4 });
            }],
            [0.95, done],
          ],
        });
        break;
      }
      // ── 얼음 ──
      case 'wave': {
        this.play(k, {
          kind, dur: 0.6,
          poses: [[0.18, 'move0'], [0.6, 'cast']],
          lunge: (u) => (u < 0.18 ? -3 : 4),
          events: [
            [0.2, () => {
              const p = this.hand(k, 52, 44);
              const tt = aimAt();
              const a0 = Math.atan2(tt.y - p.y, tt.x - p.x);
              // 세 갈래 부채꼴 — 가운데가 조금 빠르다
              for (const sp of [0, -0.22, 0.22]) {
                this.fire('wave', p.x, p.y, Math.cos(a0 + sp), Math.sin(a0 + sp), sp === 0 ? 470 : 420, 360, 30 * am, kd * 0.8, this.status('ice'));
              }
              h.burst(p.x, p.y, 40, look.attr, 'spark', look.ramp);
              audio.play('frostBlade', { rate: 0.8, gain: 1.2 });
              audio.play('iceShot', { rate: 0.7 });
            }],
            [0.6, done],
          ],
        });
        break;
      }
      case 'claw': {
        this.play(k, {
          kind, dur: 0.46,
          poses: [[0.1, 'move0'], [0.46, 'claw']],
          lunge: (u) => (u < 0.1 ? -3 : u < 0.2 ? 10 : 6),
          events: [
            [0.12, () => {
              const a = this.aim(k, aimAt(), 30);
              const hits = h.coneDamage(k.x, k.y - 30, a.dx, a.dy, 110 * am, 150, kd * 2.2, 200, this.status('ice'));
              h.burst(k.x + k.face * 62, k.y - 34, 50, look.attr, 'claw', look.ramp);
              h.burst(k.x + k.face * 80, k.y - 30, 30, look.attr, 'spark', look.ramp, 0.04);
              audio.play('frostBlade', { rate: 0.9 });
              if (hits > 0) audio.play('hitIce');
            }],
            [0.46, done],
          ],
        });
        break;
      }
      case 'spike': {
        this.play(k, {
          kind, dur: 1.0,
          poses: [[0.36, 'crouch'], [0.9, 'spike'], [1.0, 'orbFront']],
          lift: (u) => (u < 0.36 ? -2 : u < 0.9 ? 8 : 4),
          events: [
            [0.38, () => {
              const r = 140 * am;
              h.areaDamage(k.x, k.y, r, kd * 3.0, 160, 1.2, 0.5, -1, 0, this.status('ice', true));
              // 가시가 솟고 → 서리 고리가 두 겹으로 퍼진다
              h.burst(k.x, k.y - 10, r * 0.7, look.attr, 'spark', look.ramp);
              h.burst(k.x, k.y, r * 0.6, look.attr, 'ring', look.ramp, 0.02);
              h.burst(k.x, k.y, r * 1.05, look.attr, 'ring', look.ramp, 0.12);
              this.burstRing(k.x, k.y, 8, r * 0.85, 30, 'spark', look, 0.1);
              h.flashScreen(0.12, 0.2, 0x9fe8ff);
              h.shakeScreen(0.16, 5);
              h.heavyHit();
              audio.play('shatter', { rate: 0.7, gain: 1.6 });
              audio.play('frostField', { rate: 0.8 });
            }],
            [1.0, done],
          ],
        });
        break;
      }
      // ── 번개 ──
      case 'zap': {
        this.play(k, {
          kind, dur: 0.56,
          poses: [[0.16, 'idle0'], [0.56, 'bolt']],
          lunge: (u) => (u < 0.16 ? -2 : 3),
          events: [
            [0.18, () => {
              const p = this.hand(k, 96, 44);
              // 두 줄기로 갈라져 서로 다른 적에게 옮겨붙는다
              const seen = new Set<number>();
              let hits = 0;
              for (let i = 0; i < 2; i++) {
                hits += h.chainDamage({
                  x: p.x, y: p.y, firstRange: 300, jumpRange: 150,
                  damage: kd * 1.1, jumps: 4, knockback: 60, status: this.status('bolt'),
                  splash: 30, style: 'violet', splashStyle: 'spark', splashRamp: look.ramp, exclude: seen, bolt: 1,
                });
              }
              audio.play('arc', { hops: Math.max(1, hits), rate: 0.9 });
            }],
            [0.56, done],
          ],
        });
        break;
      }
      case 'crescent': {
        this.play(k, {
          kind, dur: 0.44,
          poses: [[0.08, 'idle0'], [0.44, 'crescent']],
          lunge: (u) => (u < 0.08 ? -2 : u < 0.18 ? 9 : 5),
          events: [
            [0.1, () => {
              const a = this.aim(k, aimAt(), 30);
              const hits = h.coneDamage(k.x, k.y - 30, a.dx, a.dy, 125 * am, 170, kd * 2.2, 220, this.status('bolt'));
              // 초승달을 따라 짧은 보랏빛 번개가 차례로 튄다
              const base = Math.atan2(a.dy, a.dx);
              const cx = k.x;
              const cy = k.y - 30;
              const R = 100 * am;
              const pt = (u: number, r: number): [number, number] => {
                const aa = base + (u - 0.5) * 1.7;
                return [cx + Math.cos(aa) * r, cy + Math.sin(aa) * r];
              };
              for (let i = 0; i < 4; i++) {
                const [x0, y0] = pt(i / 4, R * (0.9 + Math.random() * 0.15));
                const [x1, y1] = pt((i + 1) / 4, R * (0.9 + Math.random() * 0.15));
                h.boltLine(x0, y0, x1, y1, 'violet', 0.8, i * 0.025);
              }
              for (let i = 0; i < 3; i++) {
                const [x0, y0] = pt(0.2 + i * 0.3, R * 0.95);
                const [x1, y1] = pt(0.2 + i * 0.3, R * 1.3);
                h.boltLine(x0, y0, x1, y1, 'violet', 0.6, 0.04 + i * 0.025);
              }
              audio.play('static', { gain: 1.3 });
              audio.play('windcut', { rate: 0.8, gain: 0.6 });
              if (hits > 0) audio.play('hitShock');
            }],
            [0.44, done],
          ],
        });
        break;
      }
      case 'beam': {
        // 광선 위로 보랏빛 번개가 세 줄씩 몇 번 휘감는다
        const crackle = (n: number, scale: number) => (): void => {
          const p = this.hand(k, 30, 34);
          const len = 200 * am;
          for (let i = 0; i < n; i++) {
            const oy = (i - (n - 1) / 2) * 9;
            h.boltLine(p.x, p.y + oy, p.x + k.face * len, p.y + oy + (Math.random() - 0.5) * 10, 'violet', scale - Math.abs(oy) * 0.03, i * 0.04);
          }
        };
        this.play(k, {
          kind, dur: 1.0,
          poses: [[0.38, 'sigil'], [0.9, 'beam'], [1.0, 'idle0']],
          lift: (u) => (u < 0.38 ? 6 : 2),
          events: [
            [0.05, () => audio.play('sigil', { gain: 1.2 })],
            [0.4, () => {
              const tt = aimAt();
              k.face = tt.x >= k.x ? 1 : -1;
              const p = this.hand(k, 30, 34);
              const len = 200 * am;
              // 그림의 광선은 옆으로 곧게 뻗는다 — 판정도 바라보는 쪽으로
              h.lineDamage(p.x, p.y, k.face, 0, len, 30 * am, kd * 4.0, 200, this.status('bolt', true));
              crackle(3, 1.3)();
              for (let i = 1; i <= 4; i++) h.burst(p.x + (k.face * len * i) / 4, p.y, 28, look.attr, 'spark', look.ramp, i * 0.03);
              h.flashScreen(0.14, 0.22, 0xa98bff);
              h.shakeScreen(0.14, 5);
              h.heavyHit();
              audio.play('thunder', { rate: 1.15, gain: 0.9 });
            }],
            [0.58, crackle(2, 1.0)],
            [0.76, crackle(2, 0.8)],
            [1.0, done],
          ],
        });
        break;
      }
      case 'strike': {
        // 낙뢰 다섯 — 적이 모자라면 노리는 적 둘레에 떨어진다
        const marks: Array<{ e: Enemy | null; uid: number; x: number; y: number }> = h
          .pickTargets(k.x, k.y, 320, 5)
          .map((e) => ({ e, uid: e.uid, x: e.x, y: e.y }));
        while (marks.length < 5) {
          const a = h.rng.next() * TAU;
          const r = 60 + h.rng.next() * 80;
          marks.push({ e: null, uid: -1, x: t.x + Math.cos(a) * r, y: t.y + Math.sin(a) * r * 0.8 });
        }
        this.play(k, {
          kind, dur: 0.95,
          poses: [[0.3, 'turn'], [0.95, 'pillars']],
          lift: (u) => (u < 0.3 ? 2 : 8),
          events: [
            [0.05, () => audio.play('sigil', { gain: 1.1 })],
            ...marks.map((m, i): [number, () => void] => [0.32 + i * 0.09, () => {
              const live = m.e && m.e.uid === m.uid && !m.e.dead;
              const x = live ? m.e!.x : m.x;
              const y = live ? m.e!.y : m.y;
              this.skyStrike(x, y);
              h.boltLine(x + (Math.random() - 0.5) * 40, y - 230, x, y, 'violet', 1.2);
              h.areaDamage(x, y, 70 * am, kd * 2.0, 120, 0, 0, -1, 0, this.status('bolt', true));
              if (i === 0) {
                audio.play('thunder', { gain: 1.0 });
                h.flashScreen(0.12, 0.18, 0xa98bff);
              } else audio.play('zap', { rate: 0.7, gain: 1.3 });
              h.shakeScreen(0.1, 4);
            }]),
            [0.95, done],
          ],
        });
        break;
      }
      default:
        k.count--;
        break;
    }
  }

  /** (x, y) 둘레에 폭발 n 개를 고리로 — delay 를 주면 퍼져 나가는 것처럼 보인다 */
  private burstRing(
    x: number, y: number, n: number, r: number, size: number,
    style: 'fire' | 'spark', look: (typeof LOOK)[Element], delay: number,
  ): void {
    const off = Math.random() * TAU;
    for (let i = 0; i < n; i++) {
      const a = off + (i / n) * TAU;
      this.host.burst(x + Math.cos(a) * r, y + Math.sin(a) * r * 0.8, size, look.attr, style, look.ramp, delay);
    }
  }

  // ── 등장·순간이동 ────────────────────────────────────

  /**
   * 번쩍하는 순간 삼각 꼭짓점에 이미 떠 있다(번쩍에 가려 나타난다) — 그 자리에서 원소마다 제 몸짓.
   * 큰 소리는 updateIntro 의 REVEAL 에서 한꺼번에 내고, 여기서는 몸짓이 바뀔 때 원소 소리만 작게 얹는다.
   */
  private beginSummon(k: King): void {
    const h = this.host;
    const v = this.vertex(k.slot);
    k.x = v.x;
    k.y = v.y;
    k.vx = 0;
    k.vy = 0;
    k.mode = 'summon';
    k.alpha = 1;
    const look = LOOK[k.el];
    if (k.el === 'fire') {
      // 불기둥 속에서 힘을 모은 모습 → 불꽃이 확 일고 → 포효
      h.burst(k.x, k.y, 120, look.attr, 'fire', look.ramp);
      this.play(k, {
        kind: 'summon', dur: 1.0, poses: [[0.42, 'power'], [0.72, 'flare'], [1.0, 'roar']],
        events: [[0.42, () => audio.play('boomFire', { rate: 0.55, gain: 0.8 })]],
      });
    } else if (k.el === 'ice') {
      // 소용돌이 속에서 → 제 모습으로
      h.burst(k.x, k.y - 10, 110, look.attr, 'spark', look.ramp);
      this.play(k, {
        kind: 'summon', dur: 0.95, poses: [[0.4, 'swirl'], [0.75, 'reform'], [0.95, 'idle0']],
        events: [[0.4, () => audio.play('frostField', { rate: 0.7, gain: 0.8 })]],
      });
    } else {
      // 하늘에서 번개가 떨어지고 → 번개 고리 속에서
      this.skyStrike(k.x, k.y);
      this.play(k, {
        kind: 'summon', dur: 0.95, poses: [[0.55, 'ring'], [0.95, 'sigil']],
        events: [[0.55, () => audio.play('static', { gain: 1.1 })]],
      });
    }
  }

  /** 캐릭터가 너무 멀어졌다 — 흩어졌다가 곁에서 다시 모인다 */
  private beginBlink(k: King): void {
    const h = this.host;
    const look = LOOK[k.el];
    k.mode = 'blink';
    k.target = null;
    h.burst(k.x, k.y - 30, 60, look.attr, look.burstStyle, look.ramp);
    let moved = false;
    const lift = (u: number): number => {
      if (u < 0.16) {
        k.alpha = 1 - u / 0.16;
      } else {
        if (!moved) {
          moved = true;
          const home = this.homeOf(k.slot, h);
          k.x = home.x;
          k.y = home.y;
          k.vx = 0;
          k.vy = 0;
          h.burst(k.x, k.y - 30, 70, look.attr, look.burstStyle, look.ramp);
        }
        k.alpha = Math.min(1, (u - 0.16) / 0.18);
      }
      return 0;
    };
    const pose = IDLE[k.el][0];
    this.play(k, { kind: 'blink', dur: 0.4, poses: [[0.4, pose]], lift, events: [] });
  }

  // ── 날아가는 것 ─────────────────────────────────────

  private fire(
    kind: Shot['kind'], x: number, y: number, dx: number, dy: number, speed: number, dist: number,
    radius: number, damage: number, status: StatusApply,
  ): void {
    const art = kingArt.get(kind === 'fireball' ? 'fire' : 'ice')?.get(kind);
    if (!art) return;
    let s = this.shots.find((v) => !v.sprite.visible);
    if (!s) {
      if (this.shots.length >= 16) return;
      const sp = new Sprite(art.texture);
      this.host.fxLayer.addChild(sp);
      s = { kind, sprite: sp, x, y, vx: 0, vy: 0, life: 0, max: 0, radius, damage, status, seen: new Set(), trail: 0 };
      this.shots.push(s);
    }
    s.kind = kind;
    s.x = x;
    s.y = y;
    s.vx = dx * speed;
    s.vy = dy * speed;
    s.max = dist / speed;
    s.life = s.max;
    s.radius = radius;
    s.damage = damage;
    s.status = status;
    s.seen.clear();
    s.trail = 0.03;
    applyFrame(s.sprite, art);
    // 오른쪽으로 날아가게 그렸다 — 방향대로 돌리고, 왼쪽으로 갈 때는 위아래를 뒤집어 그림이 거꾸로 서지 않게
    s.sprite.rotation = Math.atan2(dy, dx);
    s.sprite.scale.set(1, dx < 0 ? -1 : 1);
    s.sprite.alpha = 1;
    s.sprite.visible = true;
    s.sprite.position.set(Math.round(x), Math.round(y));
  }

  private updateShots(dt: number): void {
    const h = this.host;
    for (const s of this.shots) {
      if (!s.sprite.visible) continue;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.life -= dt;
      s.sprite.position.set(Math.round(s.x), Math.round(s.y));
      // 꼬리 — 화염구는 불티, 파동은 서리 (그림 꼬리 끝쯤에)
      s.trail -= dt;
      if (s.trail <= 0) {
        const v = Math.hypot(s.vx, s.vy) || 1;
        const back = s.kind === 'fireball' ? 58 : 44;
        const jx = (Math.random() - 0.5) * 10;
        const jy = (Math.random() - 0.5) * 10;
        const tx = s.x - (s.vx / v) * back + jx;
        const ty = s.y - (s.vy / v) * back + jy;
        if (s.kind === 'fireball') {
          s.trail = 0.045;
          h.burst(tx, ty, 14, 'red', 'ember');
        } else {
          s.trail = 0.05;
          h.burst(tx, ty, 16, 'blue', 'spark', FX_RAMPS.ice);
        }
      }
      if (s.kind === 'fireball') {
        // 처음 닿은 곳(또는 날아간 끝)에서 터진다
        const hit = h.sweepDamage(s.x, s.y, 0, TAU, s.radius, s.damage, 120, s.status, s.seen);
        if (hit > 0 || s.life <= 0) {
          const r = 62 * this.areaMul;
          h.areaDamage(s.x, s.y, r, s.damage, 180, 0, 0, -1, 0, s.status);
          h.burst(s.x, s.y, r, 'red', 'fire', FX_RAMPS.flame);
          h.burst(s.x, s.y, r * 0.6, 'red', 'ember', undefined, 0.06);
          h.spawnCinder(s.x, s.y, 40, s.damage * 0.08, { burn: 2, burnDmg: this.burnDmg || undefined });
          audio.play('boomFire', { rate: 0.9 });
          s.sprite.visible = false;
        }
      } else {
        // 얼음 파동은 꿰뚫고 끝까지 간다 — 끝에서 흩어진다
        const hit = h.sweepDamage(s.x, s.y, 0, TAU, s.radius, s.damage, 90, s.status, s.seen);
        if (hit > 0 && s.seen.size === hit) audio.play('hitIce');
        const u = s.life / s.max;
        s.sprite.alpha = u < 0.25 ? u / 0.25 : 1;
        if (s.life <= 0) {
          h.burst(s.x, s.y, 36, 'blue', 'spark', FX_RAMPS.ice);
          s.sprite.visible = false;
        }
      }
    }
  }

  /** 하늘에서 번개가 떨어진 그림 (낙뢰·번개왕 등장) */
  private skyStrike(x: number, y: number): void {
    const art = kingArt.get('bolt')?.get('strike');
    if (!art) return;
    let s = this.strikes.find((v) => !v.sprite.visible);
    if (!s) {
      if (this.strikes.length >= 10) return;
      const sp = new Sprite(art.texture);
      this.host.fxLayer.addChild(sp);
      s = { sprite: sp, t: 0 };
      this.strikes.push(s);
    }
    applyFrame(s.sprite, art);
    s.sprite.scale.set(this.host.rng.next() < 0.5 ? -1 : 1, 1);
    s.sprite.position.set(Math.round(x), Math.round(y));
    s.sprite.alpha = 1;
    s.sprite.visible = true;
    s.t = 0;
    this.host.burst(x, y, 60, 'yellow', 'spark', FX_RAMPS.violet);
    this.host.burst(x, y, 44, 'yellow', 'ring', FX_RAMPS.violet, 0.05);
  }

  private updateStrikes(dt: number): void {
    for (const s of this.strikes) {
      if (!s.sprite.visible) continue;
      s.t += dt;
      // 번쩍 → 잠깐 떨림 → 사라짐
      if (s.t < 0.1) s.sprite.alpha = 0.85 + Math.random() * 0.15;
      else if (s.t < 0.42) s.sprite.alpha = 1 - (s.t - 0.1) / 0.32;
      else s.sprite.visible = false;
    }
  }
}
